// Small presentational helpers shared by every page (pure functions returning SafeHTML).
import { html, raw, attr, esc } from './singles-hub--core--html.js?v=c7c57de095';
import { money, pretty } from './singles-hub--format.js?v=c7c57de095';

// Shopify CDN width param keeps images light; other hosts unchanged.
export function sized(url, w) {
  if (!url) return null;
  if (/cdn\.shopify\.com|\/cdn\/shop\//.test(url)) return url + (url.includes('?') ? '&' : '?') + 'width=' + w;
  return url;
}

// Card image in a 63:88 frame with the branded card back underneath (shows when there is no image or it fails).
export function cardArt(url, { name = '', code = '', size = '', w = 400, alt, eager = false } = {}) {
  const cls = 'mcc-art' + (size ? ' mcc-art--' + size : '');
  // Q33: while the image loads the frame shows a shimmer; the branded back only appears after an error or a timeout
  return html`<span class="${cls}${url ? ' has-img' : ''}"><span class="mcc-back" aria-hidden="true"><span class="mcc-back__mark">MCC</span>${name ? html`<span class="mcc-back__name">${pretty(name)}</span>` : ''}${code ? html`<span class="mcc-back__code">${code}</span>` : ''}<span class="mcc-back__soon">Image coming soon</span></span>${url ? html`<img data-mcc-img src="${sized(url, w)}"${srcset(url, w)} alt="${alt != null ? alt : (name ? pretty(name) + (code ? ' ' + code : '') : '')}" loading="${eager ? 'eager' : 'lazy'}" decoding="async">` : ''}</span>`;
}

// 1x + 2x Shopify CDN sizes (operator note #4: sharper accessory and card images)
export function srcset(url, w) {
  if (!url || !/cdn\.shopify\.com|\/cdn\/shop\//.test(url)) return '';
  return raw(` srcset="${esc(sized(url, w))} 1x, ${esc(sized(url, w * 2))} 2x"`);
}
// Accessory/product photo: whole product visible (object-fit: contain) in a uniform light square frame
export function productImg(url, { alt = '', w = 600 } = {}) {
  return html`<span class="mcc-prodimg${url ? ' has-img' : ''}">${url ? html`<img data-mcc-img src="${sized(url, w)}"${srcset(url, w)} alt="${alt}" loading="lazy" decoding="async">` : html`<span class="mcc-prodimg__none">${alt}</span>`}</span>`;
}
const IMG_TIMEOUT = 10000;
// global image load/error handling (no inline handlers so it works under a strict CSP)
let imgBound = false;
export function bindImages() {
  if (imgBound) return;
  imgBound = true;
  document.addEventListener('load', (e) => { const t = e.target; if (t && t.tagName === 'IMG' && t.hasAttribute('data-mcc-img')) markImg(t, true); }, true);
  document.addEventListener('error', (e) => { const t = e.target; if (t && t.tagName === 'IMG' && t.hasAttribute('data-mcc-img')) markImg(t, false); }, true);
}
// the frame (parent) knows too: is-done hides the shimmer, is-failed reveals the branded card back
function markImg(img, ok) {
  clearTimeout(img.__mccT);
  img.classList.add(ok ? 'is-loaded' : 'is-broken');
  img.classList.remove(ok ? 'is-broken' : 'is-loaded');
  const f = img.parentNode;
  if (f && f.classList) { f.classList.add(ok ? 'is-done' : 'is-failed'); f.classList.remove(ok ? 'is-failed' : 'is-done'); }
}
function startTimer(img) {
  if (img.__mccT) return;
  img.__mccT = setTimeout(() => { if (!img.classList.contains('is-loaded')) markImg(img, false); }, IMG_TIMEOUT);
}
// a lazy image below the fold has not even started loading: start its timeout only once it is near the
// viewport, or every card further down the page shows the card back for good (30C search, 2026-10-10)
let lazyIO = null;
function watchTimeout(img) {
  if (img.__mccT || img.__mccW) return;
  if (img.loading !== 'lazy' || typeof IntersectionObserver === 'undefined') return startTimer(img);
  lazyIO = lazyIO || new IntersectionObserver((es) => es.forEach((e) => {
    if (e.isIntersecting) { lazyIO.unobserve(e.target); startTimer(e.target); }
  }), { rootMargin: '600px 0px' });
  img.__mccW = true;
  lazyIO.observe(img);
}
// images restored from cache can fire load before the listener sees them
export function settleImages(root) {
  (root || document).querySelectorAll('img[data-mcc-img]:not(.is-loaded):not(.is-broken)').forEach((img) => {
    if (img.complete) markImg(img, !!img.naturalWidth);
    else watchTimeout(img);
  });
  // morph keeps frames but may rebuild their class attribute: re-sync frames of already-settled images
  (root || document).querySelectorAll('img[data-mcc-img].is-loaded, img[data-mcc-img].is-broken').forEach((img) => {
    const f = img.parentNode; const ok = img.classList.contains('is-loaded');
    if (f && f.classList && !f.classList.contains(ok ? 'is-done' : 'is-failed')) f.classList.add(ok ? 'is-done' : 'is-failed');
  });
}

export const icon = {
  search: raw('<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/></svg>'),
  chev: raw('<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" aria-hidden="true"><path d="m6 9 6 6 6-6"/></svg>'),
  clip: raw('<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><rect x="8" y="3" width="8" height="4" rx="1"/><path d="M16 5h2a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h2"/></svg>'),
  dots: raw('<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="5" cy="12" r="2"/><circle cx="12" cy="12" r="2"/><circle cx="19" cy="12" r="2"/></svg>'),
  gear: raw('<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M4 6h10M18 6h2M4 12h4M12 12h8M4 18h12M20 18h0"/><circle cx="16" cy="6" r="2"/><circle cx="10" cy="12" r="2"/><circle cx="18" cy="18" r="2"/></svg>'),
};

export function tierChip(t) { return t ? html`<span class="mcc-tier mcc-tier--${t}">Tier ${t}</span>` : ''; }
export function trend(n) {
  if (n == null) return '';
  const cls = n > 0 ? 'up' : n < 0 ? 'down' : 'flat';
  const t = n > 0 ? `▲ ${n}` : n < 0 ? `▼ ${-n}` : '— 0';
  const pts = (k) => `${k} point${k === 1 ? '' : 's'}`;
  const sr = n > 0 ? `up ${pts(n)} vs previous period` : n < 0 ? `down ${pts(-n)} vs previous period` : 'no change vs previous period';
  return html`<span class="mcc-trend mcc-trend--${cls}"><span aria-hidden="true">${t}</span><span class="mcc-sr"> (${sr})</span></span>`;
}
export function regChip(mark) { return mark ? html`<span class="mcc-reg">Reg.&nbsp;${mark}</span>` : ''; }
export function legalPill(legal) { return legal ? html`<span class="mcc-pill mcc-pill--ok">Standard legal</span>` : html`<span class="mcc-pill mcc-pill--bad">Not Standard legal (rotated)</span>`; }

// "4 copies in stock" / "Only 1 copy left" / "2 already in your basket" / "Sold out" (one display rule, Q1/Q21)
// n = copies_available (store stock minus what is already in the basket), inBasket = copies of it in the basket
export function stockText(n, inBasket = 0) {
  if (n >= 4) return `${n} copies in stock`;
  if (n > 0) return `Only ${n} ${n === 1 ? 'copy' : 'copies'} left`;
  if (inBasket > 0) return `${inBasket} already in your basket`;
  return 'Sold out';
}
export function stockPill(n, inBasket = 0) {
  if (n >= 4) return html`<span class="mcc-pill mcc-pill--ok">✓ ${stockText(n)}</span>`;
  if (n > 0) return html`<span class="mcc-pill mcc-pill--warn">${stockText(n)}</span>`;
  if (inBasket > 0) return html`<span class="mcc-pill mcc-pill--ink">${stockText(0, inBasket)}</span>`;
  return html`<span class="mcc-pill mcc-pill--bad">Sold out</span>`;
}

// Coverage/stock bar. segs: [{n, color, label}], total
export function bar(segs, total, { size = '', label = '' } = {}) {
  const t = Math.max(1, total);
  return html`<div class="mcc-bar ${size ? 'mcc-bar--' + size : ''}" role="img" aria-label="${label}">${segs.filter((s) => s.n > 0).map((s) => html`<span style="width:${((s.n / t) * 100).toFixed(2)}%;background:${s.color}"></span>`)}</div>`;
}

// Live buildability line, one wording everywhere (glossary Q26): "We can fill 42/60 · £63.24" / "Whole deck in stock · £x"
export function buildLine(b) {
  if (!b) return 'No list published yet';
  if (b.text) return b.text;
  if (b.complete || b.filled >= b.deck_size) return `Whole deck in stock · ${money(b.price)}`;
  return `We can fill ${b.filled}/${b.deck_size || 60} · ${money(b.price)}`;
}
// Buildability bar + its label, always together (Q16: never next to the meta share)
export function buildBar(b, accent, { size = '' } = {}) {
  if (!b) return html`<div class="mcc-build"><span class="mcc-build__t mcc-muted">No list published yet</span>${bar([], 60, { size, label: 'No list yet' })}</div>`;
  return html`<div class="mcc-build"><span class="mcc-build__t">${buildLine(b)}</span>${bar([{ n: b.filled, color: accent || '#D61463' }], b.deck_size || 60, { size, label: `We can fill ${b.filled} of ${b.deck_size || 60} cards` })}</div>`;
}
// "To fix" explainer (glossary Q31)
export const TO_FIX_HELP = 'Cards we can fill with a swap or another printing, once you OK it.';

export function dialog({ id, title, body, foot, size = '', side = false, sheetClass = '', labelId }) {
  const lid = labelId || id + '-title';
  return html`<div class="mcc-overlay ${side ? 'mcc-overlay--side' : ''}" data-click="overlayClick" data-dialog-id="${id}">
    <div class="${side ? 'mcc-sheet ' + sheetClass : 'mcc-modal ' + (size ? 'mcc-modal--' + size : '')}" role="dialog" aria-modal="true" aria-labelledby="${lid}" id="${id}" data-mcc-dialog data-lock>
      <div class="mcc-dlg-head"><h2 id="${lid}">${title}</h2><button type="button" class="mcc-close" data-dialog-close data-click="closeDialog" data-dialog-id="${id}" aria-label="Close">×</button></div>
      <div class="mcc-dlg-body">${body}</div>
      ${foot ? html`<div class="mcc-dlg-foot">${foot}</div>` : ''}
    </div></div>`;
}
// Standard overlay click handler: click on the dim backdrop closes
export function overlayClose(el, e, close) { if (e.target === el) close(el.getAttribute('data-dialog-id')); }

// data = { k: 's3', max: 4 } -> data-k="s3" data-max="4" (names are checked, values escaped; never raw HTML)
export function dataAttrs(data) {
  if (!data || typeof data !== 'object') return '';
  return raw(Object.entries(data).filter(([n, v]) => /^[a-z][a-z0-9-]*$/.test(n) && v !== null && v !== undefined)
    .map(([n, v]) => ` data-${n}="${esc(v)}"`).join(''));
}

// Add-to-basket buttons: disabled + "Adding…" spinner while the cart calls run (they take 1-2 s: stock
// revalidate, add, re-read). Without it a tap looked like nothing happened until the toast (2026-10-16).
export async function busy(btn, fn, label = 'Adding…') {
  if (!btn || btn.getAttribute('aria-busy') === 'true') return btn ? undefined : fn();
  const prev = btn.innerHTML;
  btn.setAttribute('aria-busy', 'true');
  btn.disabled = true;
  btn.classList.add('mcc-btn--busy');
  btn.innerHTML = `<span class="mcc-spin" aria-hidden="true"></span>${label}`;
  try { return await fn(); } finally {
    if (btn.isConnected && btn.getAttribute('aria-busy') === 'true') {
      btn.removeAttribute('aria-busy'); btn.disabled = false; btn.classList.remove('mcc-btn--busy');
      if (btn.querySelector('.mcc-spin')) btn.innerHTML = prev;
    }
  }
}
export function stepper({ value, min = 1, max = 99, minus, plus, label = 'quantity', data = null }) {
  const d = dataAttrs(data);
  return html`<span class="mcc-stepper" role="group" aria-label="${label}"><button type="button" data-click="${minus}"${d} aria-label="One fewer"${attr('disabled', value <= min)}>−</button><output aria-live="polite">${value}</output><button type="button" data-click="${plus}"${d} aria-label="One more"${attr('disabled', value >= max)}>+</button></span>`;
}

export function crumbs(items) {
  return html`<nav class="mcc-crumbs" aria-label="Breadcrumb">${items.map((it, i) => html`${i ? html`<span aria-hidden="true">›</span>` : ''}${it.href ? html`<a href="${it.href}">${it.t}</a>` : html`<span aria-current="page">${it.t}</span>`}`)}</nav>`;
}

// One owner per page heading: inside the theme (data-mcc-embedded="true") the Liquid section renders the
// SEO h1 + intro server-side, so the app renders the same title as an h2 (demote) or not at all (drop).
export function pageTitle(embedded, content, { cls = 'mcc-h1', id = '', drop = false, style = '' } = {}) {
  if (embedded && drop) return '';
  const a = html`${id ? html` id="${id}"` : ''} class="${cls}"${style ? html` style="${style}"` : ''}`;
  return embedded ? html`<h2${a}>${content}</h2>` : html`<h1${a}>${content}</h1>`;
}
// Engine shapes: {lo,hi} ranges and "1–2" strings both render as text
export const rangeText = (x) => (x && typeof x === 'object' ? (x.lo === x.hi ? String(x.lo) : `${x.lo}–${x.hi}`) : String(x == null ? '' : x));

export function sampleTag(on) { return on ? html`<span class="mcc-pill mcc-pill--sample">Sample data</span>` : ''; }

// One engine-off message set (Q14)
export const OFFLINE = {
  status: 'Deck matching is offline right now',
  banner: 'Card search and deck matching are offline. You can still browse sets and the shop’s normal search.',
};
export function offlineNotice(text) {
  return html`<div class="mcc-notice mcc-notice--warn" role="status"><div><strong>${OFFLINE.status}.</strong>${text || OFFLINE.banner}</div></div>`;
}

// Columns that never strand a single orphan: choose the count c<=max so the last row is not a lone item.
export function balancedCols(n, max, min = 2) {
  if (n <= max) return Math.max(Math.min(n, max), 1);
  for (let c = max; c >= min; c--) { const r = n % c; if (r === 0 || r >= Math.ceil(c / 2)) return c; }
  return max;
}
